import { useCallback, useRef, useState, useEffect } from "react";
import _ from "lodash";

/**
 * useLongPress
 * Pointer-based hook that distinguishes short click vs long press.
 * Provides isHolding flag for UI feedback during long press delay.
 */

export const useLongPress = ({
  onLongPress,
  onClick,
  pressTime = 1000,
  pressTimeShort = 200,
  moveTolerance = 10,
  onRelease,
  uiHoldDelay = 400,
  onLongPressShort
}) => {

  const timerRef = useRef(null);
  const uiTimerRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0 });

  const activeRef = useRef(false);
  const movedRef = useRef(false);
  const longTriggeredRef = useRef(false);
  const shortTriggeredRef = useRef(false);

  const callbacksRef = useRef({});
  callbacksRef.current = { onLongPress, onClick, onRelease, onLongPressShort };

  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressShort, setProgressShort] = useState(0);

  const clearTimers = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(uiTimerRef.current);
    timerRef.current = null;
    uiTimerRef.current = null;
  }, []);

  const reset = useCallback((reason) => {
    clearTimers();

    activeRef.current = false;

    setIsHolding(false);
    setProgress(0);
    setProgressShort(0);

    if (shortTriggeredRef.current) {
      shortTriggeredRef.current = false;
      callbacksRef.current.onLongPressShort?.(false);
    }
  }, [clearTimers]);

  const start = useCallback((e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (activeRef.current) return;

    clearTimers();

    activeRef.current = true;
    movedRef.current = false;
    longTriggeredRef.current = false;
    shortTriggeredRef.current = false;

    startRef.current = {
      x: e.clientX,
      y: e.clientY
    };

    setIsHolding(false);
    setProgress(0);
    setProgressShort(0);

    const pointerEvent = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      clientX: e.clientX,
      clientY: e.clientY
    };

    const startTime = performance.now();

    uiTimerRef.current = setTimeout(() => {
      if (activeRef.current && !movedRef.current) {
        setIsHolding(true);
      }
    }, uiHoldDelay);

    timerRef.current = setInterval(() => {

      if (!activeRef.current || movedRef.current) return;

      const elapsed = performance.now() - startTime;

      const pct = Math.min(elapsed / pressTime, 1);
      const pctShort = Math.min(elapsed / pressTimeShort, 1);

      setProgress(pct);
      setProgressShort(pctShort);

      // SHORT PRESS THRESHOLD - fires only once
      if (pctShort >= 1 && !shortTriggeredRef.current) {
        shortTriggeredRef.current = true;
        callbacksRef.current.onLongPressShort?.(true);
      }

      // LONG PRESS THRESHOLD - fires only once
      if (pct >= 1 && !longTriggeredRef.current) {
        longTriggeredRef.current = true;

        reset('onLongPress');

        callbacksRef.current.onLongPress?.(pointerEvent);
      }

    }, 16);

  }, [pressTime, pressTimeShort, uiHoldDelay, clearTimers, reset]);

  const move = useCallback((e) => {
    if (!activeRef.current || movedRef.current) return;

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;

    if (Math.hypot(dx, dy) > moveTolerance) {
      movedRef.current = true;
      reset('move');
    }
  }, [moveTolerance, reset]);

  const end = useCallback((e) => {
    if (!activeRef.current && !longTriggeredRef.current) return;

    const wasLongPress = longTriggeredRef.current;
    const wasShortPress = shortTriggeredRef.current;
    const wasMoved = movedRef.current;

    reset('end');

    // Only trigger click if NO movement or press threshold
    if (!wasMoved && !wasShortPress && !wasLongPress) {
      callbacksRef.current.onClick?.(e);
    }

    // Only release if interaction wasn't canceled by movement
    if (!wasMoved) {
      callbacksRef.current.onRelease?.(e);
    }

    longTriggeredRef.current = false;
  }, [reset]);

  const cancel = useCallback(() => {
    if (!activeRef.current && !longTriggeredRef.current) return;

    reset('cancel');
    longTriggeredRef.current = false;
    movedRef.current = true;

    callbacksRef.current.onRelease?.();
  }, [reset]);

  useEffect(() => () => {
    clearTimers();
  }, [clearTimers]);

  return {
    onPointerDown: start,
    onPointerMove: move,
    onPointerUp: end,
    onPointerCancel: cancel,
    style: {
      // touchAction: "manipulation",
      touchAction: "pan-y",
      userSelect: "none"
    },
    isHolding: isHolding ? true : undefined,
    progress,
    progressShort
  };
};